import React, { useState } from 'react';
import SubscriptionVerificationCard from '../ui/SubscriptionVerificationCard';
import { verifySubscription } from '../../features/dashboard/services/dashboardService';
import { Loader2 } from 'lucide-react';

/**
 * Demo component showing how to use SubscriptionVerificationCard
 * with real API responses from /api/v1/payments/verify-subscription/
 */
const SubscriptionVerificationDemo = () => {
  const [verificationData, setVerificationData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Sample API response for testing
  const sampleResponse = {
    success: true,
    message: "Subscription confirmed and activated",
    data: {
      status: "active",
      plan_status: "active",
      is_paid: true,
      current_plan_id: "c7d9fbdb-eca9-4b5c-bcc4-a8550f58ba61",
      subscription_id: "sub_1TKvqA2R2ORFSVmD4PxfRNEd",
      plan: {
        id: "c7d9fbdb-eca9-4b5c-bcc4-a8550f58ba61",
        name: "Business",
        price: "99.00"
      }
    }
  };

  const handleVerifySubscription = async (subscriptionId) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await verifySubscription(subscriptionId);
      setVerificationData(response);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadSampleData = () => {
    setVerificationData(sampleResponse);
    setError(null);
  };

  const clearData = () => {
    setVerificationData(null);
    setError(null);
  };

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6">
      <div className="text-center">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Subscription Verification Card Demo</h1>
        <p className="text-gray-600">Test the SubscriptionVerificationCard component with API responses</p>
      </div>

      {/* Controls */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <h2 className="font-semibold mb-3">Test Options</h2>
        <div className="flex flex-wrap gap-3">
          <button
            onClick={loadSampleData}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Load Sample Response
          </button>
          
          <button
            onClick={() => handleVerifySubscription('sub_1TKvqA2R2ORFSVmD4PxfRNEd')}
            disabled={loading}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors flex items-center gap-2"
          >
            {loading && <Loader2 className="w-4 h-4 animate-spin" />}
            Test Real API Call
          </button>
          
          <button
            onClick={clearData}
            className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
          >
            Clear
          </button>
        </div>
        
        {error && (
          <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
            <strong>Error:</strong> {error}
          </div>
        )}
      </div>

      {/* Cards Display */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Full Card */}
        {verificationData && (
          <div>
            <h3 className="font-semibold mb-3">Full Card View</h3>
            <SubscriptionVerificationCard data={verificationData} />
          </div>
        )}

        {/* Compact Card */}
        {verificationData && (
          <div>
            <h3 className="font-semibold mb-3">Compact Card View</h3>
            <SubscriptionVerificationCard data={verificationData} compact={true} />
          </div>
        )}
      </div>

      {/* API Response JSON */}
      {verificationData && (
        <div className="bg-gray-900 text-white rounded-lg p-4">
          <h3 className="text-white font-semibold mb-3">API Response JSON</h3>
          <pre className="text-xs overflow-x-auto">
            {JSON.stringify(verificationData, null, 2)}
          </pre>
        </div>
      )}

      {/* Usage Examples */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <h3 className="font-semibold mb-3">Usage Examples</h3>
        <div className="space-y-2 text-sm">
          <div className="bg-white p-3 rounded border">
            <strong>Full Card:</strong>
            <code className="block mt-1 text-xs bg-gray-100 p-2 rounded">
              {'<SubscriptionVerificationCard data={apiResponse} />'}
            </code>
          </div>
          
          <div className="bg-white p-3 rounded border">
            <strong>Compact Card:</strong>
            <code className="block mt-1 text-xs bg-gray-100 p-2 rounded">
              {'<SubscriptionVerificationCard data={apiResponse} compact={true} />'}
            </code>
          </div>
          
          <div className="bg-white p-3 rounded border">
            <strong>With Custom Classes:</strong>
            <code className="block mt-1 text-xs bg-gray-100 p-2 rounded">
              {'<SubscriptionVerificationCard data={apiResponse} className="shadow-lg" />'}
            </code>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionVerificationDemo;